import { useCallback, useEffect, useRef, useState } from "react";
import {
  downloadWins,
  fetchGigSources,
  fetchInsightsData,
  fetchRemindersStatus,
  fetchWins,
  refreshGigSources,
  saveGigFeeds,
  saveGigPosts,
  setWinChecked,
  testReminder,
  viewWinProof,
  type DirectPost,
  type GigSourcesView,
  type PartnerFeed,
  type WinAdminRow,
} from "../../api/gigAdmin";
import type { ViewedFile } from "../../api/admin";
import type { UserRole } from "../../api/types";
import { AdminShell } from "../../components/admin/AdminShell";
import { ToolbarButton } from "../../components/admin/AdminUi";
import { FileViewer } from "../../components/admin/FileViewer";
import { Banner } from "../../components/Banner";
import { ScreenState } from "../../components/ScreenState";
import { messageOf } from "../../lib/errors";
import { useSession } from "../../lib/session";
import { useAsync } from "../../lib/useAsync";
import { GigSourcesScreen } from "../../screens/admin/GigSourcesScreen";
import { InsightsScreen } from "../../screens/admin/InsightsScreen";
import { WinsScreen } from "../../screens/admin/WinsScreen";
import { useListState, useStickyData } from "./AdminRoutes";

/* Gig managers change sources, feeds and posts; MERL reads them. */
const GIG_MANAGERS: UserRole[] = ["SuperAdmin", "GigAdmin"];
const FINANCE_READERS: UserRole[] = ["SuperAdmin", "FinanceAdmin", "Merl"];

/** How often the sources page re-reads status while a refresh runs. */
export const SOURCES_POLL_MS = 5000;

export function WinsRoute() {
  const { user } = useSession();
  const canCheck = user?.role === "Merl";
  const list = useListState<{ search: string }>({ search: "" });
  const { query, page } = list;
  const loaded = useStickyData(
    () => fetchWins({ search: query.search, pageNumber: page }),
    [query, page],
  );
  // Checks saved here, laid over the loaded page until the next load.
  const [patched, setPatched] = useState<Record<string, WinAdminRow>>({});
  const [saving, setSaving] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [proof, setProof] = useState<WinAdminRow | null>(null);

  useEffect(() => setPatched({}), [loaded.data]);

  const data = loaded.data
    ? {
        ...loaded.data,
        checked:
          loaded.data.checked +
          Object.values(patched).reduce((n, row) => {
            const before = loaded.data?.rows.find((r) => r.id === row.id);
            return (
              n +
              (Boolean(row.checkedAt) === Boolean(before?.checkedAt) ? 0 : row.checkedAt ? 1 : -1)
            );
          }, 0),
        rows: loaded.data.rows.map((r) => patched[r.id] ?? r),
      }
    : null;

  const loadProof = useCallback(
    (): Promise<ViewedFile> =>
      proof ? viewWinProof(proof.id) : Promise.reject(new Error("No proof")),
    [proof],
  );

  return (
    <AdminShell
      title="Reported wins"
      actions={
        <>
          <ToolbarButton onClick={loaded.reload}>Refresh</ToolbarButton>
          <ToolbarButton
            disabled={exporting}
            onClick={() => {
              setExporting(true);
              setError(null);
              downloadWins()
                .catch((e: unknown) => setError(messageOf(e)))
                .finally(() => setExporting(false));
            }}
          >
            {exporting ? "Preparing..." : "Export wins CSV"}
          </ToolbarButton>
        </>
      }
    >
      {error ? <Banner tone="bad">{error}</Banner> : null}
      <ScreenState
        loading={loaded.loading && !loaded.data}
        error={loaded.data ? null : loaded.error}
        onRetry={loaded.reload}
      >
        <WinsScreen
          data={data}
          loading={loaded.loading}
          search={list.filter.search}
          page={page}
          canCheck={canCheck}
          saving={saving}
          onSearch={(search) => list.onFilter({ search })}
          onPage={list.setPage}
          onViewProof={setProof}
          onCheck={(win, checked) => {
            if (!canCheck) return;
            setError(null);
            setSaving((s) => [...s, win.id]);
            setWinChecked(win.id, checked)
              .then((row) => setPatched((p) => ({ ...p, [row.id]: row })))
              .catch((e: unknown) => setError(messageOf(e)))
              .finally(() => setSaving((s) => s.filter((id) => id !== win.id)));
          }}
        />
      </ScreenState>
      {proof ? (
        <FileViewer
          title={`${`${proof.firstName} ${proof.lastName}`.trim() || proof.email}: proof of payment`}
          subtitle={proof.what}
          load={loadProof}
          onClose={() => setProof(null)}
        />
      ) : null}
    </AdminShell>
  );
}

export function GigSourcesRoute() {
  const { user } = useSession();
  const canManage = Boolean(user && GIG_MANAGERS.includes(user.role));
  const loaded = useAsync(fetchGigSources, []);
  const reminders = useAsync(fetchRemindersStatus, []);
  const [view, setView] = useState<GigSourcesView | null>(null);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [saving, setSaving] = useState<"feeds" | "posts" | null>(null);
  const [saveError, setSaveError] = useState<{ list: "feeds" | "posts"; message: string } | null>(
    null,
  );
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ tone: "good" | "bad"; message: string } | null>(
    null,
  );
  // A refresh asked for here counts as running until the next status read says otherwise.
  const [asked, setAsked] = useState(false);
  const live = useRef(true);

  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);
  useEffect(() => {
    if (loaded.data) setView(loaded.data);
  }, [loaded.data]);

  const refreshing = asked || Boolean(view?.refreshing);

  useEffect(() => {
    if (!refreshing) return;
    const timer = window.setInterval(() => {
      fetchGigSources()
        .then((next) => {
          if (!live.current) return;
          setView(next);
          setAsked(false);
          if (!next.refreshing) setRefreshingId(null);
        })
        .catch(() => undefined);
    }, SOURCES_POLL_MS);
    return () => window.clearInterval(timer);
  }, [refreshing]);

  const save = async <T,>(
    list: "feeds" | "posts",
    items: T[],
    run: (items: T[]) => Promise<GigSourcesView>,
  ): Promise<boolean> => {
    setSaving(list);
    setSaveError(null);
    try {
      setView(await run(items));
      return true;
    } catch (e) {
      setSaveError({ list, message: messageOf(e) });
      return false;
    } finally {
      setSaving(null);
    }
  };

  return (
    <AdminShell title="Gig Board sources">
      <ScreenState
        loading={loaded.loading && !view}
        error={view ? null : loaded.error}
        onRetry={loaded.reload}
      >
        {view ? (
          <GigSourcesScreen
            view={view}
            reminders={reminders.data}
            remindersError={reminders.error ? reminders.error.message : null}
            canManage={canManage}
            refreshing={refreshing}
            refreshingId={refreshing ? refreshingId : null}
            refreshError={refreshError}
            saving={saving}
            saveError={saveError}
            testing={testing}
            testResult={testResult}
            onRefresh={(sourceId) => {
              if (!canManage) return;
              setRefreshError(null);
              refreshGigSources(sourceId)
                .then(() => {
                  setRefreshingId(sourceId ?? "all");
                  setAsked(true);
                })
                .catch((e: unknown) => setRefreshError(messageOf(e)));
            }}
            onSaveFeeds={(feeds: PartnerFeed[]) =>
              canManage ? save("feeds", feeds, saveGigFeeds) : Promise.resolve(false)
            }
            onSavePosts={(posts: DirectPost[]) =>
              canManage ? save("posts", posts, saveGigPosts) : Promise.resolve(false)
            }
            onTestReminder={(email) => {
              if (!canManage) return;
              setTesting(true);
              setTestResult(null);
              testReminder(email)
                .then((r) =>
                  setTestResult({
                    tone: r.sent ? "good" : "bad",
                    message: `Sent to ${r.sent} of ${r.devices} device${r.devices === 1 ? "" : "s"}.`,
                  }),
                )
                .catch((e: unknown) => setTestResult({ tone: "bad", message: messageOf(e) }))
                .finally(() => setTesting(false));
            }}
          />
        ) : null}
      </ScreenState>
    </AdminShell>
  );
}

export function InsightsRoute() {
  const { user } = useSession();
  const loaded = useAsync(fetchInsightsData, []);
  const showFinance = Boolean(user && FINANCE_READERS.includes(user.role));
  return (
    <AdminShell
      title="Insights"
      actions={<ToolbarButton onClick={loaded.reload}>Refresh</ToolbarButton>}
    >
      <ScreenState
        loading={loaded.loading && !loaded.data}
        error={loaded.data ? null : loaded.error}
        onRetry={loaded.reload}
      >
        <InsightsScreen rows={loaded.data?.rows ?? []} showFinance={showFinance} />
      </ScreenState>
    </AdminShell>
  );
}
